import { ChevronLeft, Map as MapIcon, Zap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import EnergyCertificateSection from '../components/EnergyCertificateSection';
import ErrorPanel from '../components/ErrorPanel';
import HomeInteriorSection from '../components/HomeInteriorSection';
import HomeServicesSection from '../components/HomeServicesSection';
import NearbySchoolsSection from '../components/NearbySchoolsSection';
import CouncilTaxSection from '../components/CouncilTaxSection';
import NearbyTransportSection from '../components/NearbyTransportSection';
import ReportSection from '../components/report/ReportSection';
import ReportSectionGroup from '../components/report/ReportSectionGroup';
import SurroundingsMap from '../components/SurroundingsMap';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import UnlockPanel from '../components/UnlockPanel';
import CategorySection from '../components/report/CategorySection';
import KeyFacts from '../components/report/KeyFacts';
import { ChecklistCard, SourcesCard } from '../components/report/ReportExtras';
import ReportAside from '../components/report/ReportAside';
import ReportActions from '../components/report/ReportActions';
import ReportHead from '../components/report/ReportHead';
import ReportVerdict, { KeyFindings } from '../components/report/ReportVerdict';
import ScoreMethod from '../components/report/ScoreMethod';
import ScoreTiles, { LockedTiles } from '../components/report/ScoreTiles';
import { useComparison } from '../hooks/ComparisonContext';
import { useTour } from '../hooks/TourContext';
import { tx } from '../i18n';
import { ApiError, api } from '../lib/api';
import { num } from '../lib/values';
import { categoryShort, checklist, orderedCategories } from '../lib/report';
import { rememberVisit } from '../lib/saved';
import { rememberAnalysisKey } from '../lib/session';
import type { AnalysisDetailResponse, AnalysisSummaryResponse } from '../types/api';

/**
 * One home's report, free tier and paid tier on the same page.
 *
 * <p>The page answers in the order a reader asks: the verdict and what it means, then the findings
 * that decided it, then the category scores, then every section folded away for whoever wants the
 * evidence. The free summary always loads; the detail is then attempted, and a 403 is not an error
 * here - it is the normal state for a home nobody has unlocked, so it swaps the evidence for the
 * unlock panel.
 */
export default function ReportPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { comparison } = useComparison();

  const [summary, setSummary] = useState<AnalysisSummaryResponse | null>(null);
  const [detail, setDetail] = useState<AnalysisDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<unknown>(null);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!analysisId) {
      setError(
        new ApiError(
          0,
          'ANALYSIS_NOT_FOUND',
          tx(
            'That report link is incomplete.',
            'Bu rapor bağlantısı eksik.',
            'Ten link do raportu jest niepełny.',
            'Este link do relatório está incompleto.',
            'Este enlace del informe está incompleto.',
          ),
        ),
      );
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    let pollTimer: number | null = null;
    let pollAttempts = 0;

    const loadDetail = () => {
      api
        .detail(analysisId, controller.signal)
        .then((result) => {
          if (cancelled) {
            return;
          }
          setDetail(result);
          setAwaitingPayment(false);
        })
        .catch((cause: unknown) => {
          if (cancelled || controller.signal.aborted) {
            return;
          }
          if (cause instanceof ApiError && cause.isPaymentPending) {
            pollAttempts += 1;
            if (pollAttempts <= 20) {
              setAwaitingPayment(true);
              pollTimer = window.setTimeout(loadDetail, 2000);
              return;
            }
            setAwaitingPayment(false);
            setError(cause);
            return;
          }
          // Locked, exhausted or expired: the unlock panel is the right answer, not an error.
          setDetail(null);
          setAwaitingPayment(false);
        });
    };

    setLoading(true);
    setError(null);

    api
      .summary(analysisId, controller.signal)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setSummary(result);
        rememberVisit(result);
        loadDetail();
      })
      .catch((cause: unknown) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        setError(cause);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimer !== null) {
        window.clearTimeout(pollTimer);
      }
    };
  }, [analysisId, attempt]);

  /**
   * Asks every live source again for this address and opens the report that comes back.
   *
   * <p>A report is a snapshot: crime figures move monthly, a new certificate can be lodged the
   * week after a viewing. Refreshing is therefore a normal thing to want, and it is explicit
   * rather than automatic, so a reader who has sent somebody a link knows what that link shows.
   */
  const refresh = useCallback(() => {
    if (!summary) {
      return;
    }
    setRefreshing(true);
    setRefreshError(null);
    api
      .createAnalysis({
        postcode: summary.postcode,
        addressLine: summary.addressLine,
        refresh: true,
      })
      .then((result) => {
        rememberAnalysisKey(result.analysisId, result.accessKey);
        rememberVisit(result);
        if (result.analysisId === summary.analysisId) {
          // The same report came back; reload it in place rather than navigating to itself.
          setAttempt((value) => value + 1);
          return;
        }
        window.location.assign(`/report/${result.analysisId}`);
      })
      .catch(setRefreshError)
      .finally(() => setRefreshing(false));
  }, [summary]);

  /*
   * The tour of the report, shown the first time one is open.
   *
   * This is where the tour earns its place: the home screen holds a box and a promise, and this
   * page holds everything the promise was about. Six steps are declared and five are ever drawn -
   * the locked tiles and the unlocked ones are the same step asked twice, and only one of them is
   * on any given report. A step whose element is not on the page is dropped rather than drawn
   * empty, which is also what makes this safe when a section is missing.
   *
   * Declared before the loading and error returns below, because a hook cannot be conditional,
   * and held back by {@code ready} until there is something on the page to point at.
   */
  useTour({
    id: 'report',
    ready: !loading && summary !== null,
    steps: [
      {
        anchor: 'verdict',
        title: tx(
          'The score, and what it is against',
          'Puan ve neye göre verildiği',
          'Ocena i wobec czego',
          'A pontuação e em relação a quê',
          'La puntuación y frente a qué',
        ),
        body: tx(
          'One number out of 100, measured against other homes in the same English region. A source that could not be read carries no weight rather than counting as a zero.',
          'Aynı İngiltere bölgesindeki diğer konutlara göre 100 üzerinden tek bir puan. Okunamayan bir kaynak sıfır sayılmaz; hiç ağırlık taşımaz.',
          'Jedna liczba na 100, mierzona wobec innych mieszkań w tym samym regionie Anglii. Źródło, którego nie udało się odczytać, nie ma wagi, zamiast liczyć się jako zero.',
          'Um número em 100, medido face a outras casas da mesma região de Inglaterra. Uma fonte que não pôde ser lida não tem peso, em vez de contar como zero.',
          'Un número sobre 100, medido frente a otras viviendas de la misma región de Inglaterra. Una fuente que no se ha podido leer no pesa nada, en lugar de contar como un cero.',
        ),
      },
      {
        anchor: 'key-facts',
        title: tx('The facts, free', 'Temel bilgiler, ücretsiz', 'Fakty, bezpłatnie', 'Os factos, grátis', 'Los datos, gratis'),
        body: tx(
          'Enough to decide whether the viewing is worth your afternoon, before you have paid anything.',
          'Hiçbir ödeme yapmadan, ev gezisinin öğleden sonranıza değip değmeyeceğine karar vermeye yeter.',
          'Tyle, by ocenić, czy warto poświęcić popołudnie na oglądanie — jeszcze przed zapłatą.',
          'O suficiente para decidir se a visita vale a sua tarde, antes de pagar qualquer coisa.',
          'Suficiente para decidir si la visita merece tu tarde, antes de pagar nada.',
        ),
      },
      {
        // The source line when a section is already open, and the folded section itself when not.
        anchor: ['provenance', 'section'],
        title: tx(
          'Where each number comes from',
          'Her rakam nereden geliyor',
          'Skąd pochodzi każda liczba',
          'De onde vem cada número',
          'De dónde sale cada cifra',
        ),
        body: tx(
          'Open a section and the line at the foot of it names the source, when that source published, and when we read it. Where a figure is estimated or missing, it says so instead of filling the gap.',
          'Bir bölümü açtığınızda altındaki satır kaynağı, o kaynağın yayım tarihini ve bizim okuduğumuz tarihi yazar. Bir değer tahminiyse ya da eksikse, boşluğu doldurmak yerine bunu söyler.',
          'Rozwiń sekcję, a linijka u jej dołu poda źródło, datę jego publikacji i datę odczytu. Jeśli liczba jest szacunkowa albo jej brak, sekcja to mówi, zamiast wypełniać lukę.',
          'Abra uma secção e a linha no fundo indica a fonte, quando essa fonte publicou e quando a lemos. Quando um valor é estimado ou está em falta, di-lo em vez de preencher a lacuna.',
          'Abre una sección y la línea del pie indica la fuente, cuándo publicó y cuándo la leímos. Si una cifra es estimada o falta, lo dice en lugar de rellenar el hueco.',
        ),
      },
      {
        // Only on a report nobody has unlocked yet.
        anchor: 'unlock',
        title: tx(
          'What the full report adds',
          'Tam raporun eklediği',
          'Co dodaje pełny raport',
          'O que o relatório completo acrescenta',
          'Qué añade el informe completo',
        ),
        body: tx(
          'The evidence behind each category, where this home sits in its region, and a one page PDF to take to the viewing.',
          'Her kategorinin arkasındaki kanıtlar, bu konutun bölgesindeki yeri ve ev gezisine götüreceğiniz tek sayfalık PDF.',
          'Dowody za każdą kategorią, miejsce tego mieszkania w regionie i jednostronicowy PDF do zabrania na oglądanie.',
          'As provas por trás de cada categoria, a posição desta casa na sua região e um PDF de uma página para levar à visita.',
          'Las pruebas de cada categoría, el lugar de esta vivienda en su región y un PDF de una página para llevar a la visita.',
        ),
      },
      {
        // And only on one that somebody has.
        anchor: 'score-tiles',
        title: tx(
          'Each category, and the section behind it',
          'Her kategori ve arkasındaki bölüm',
          'Każda kategoria i sekcja za nią',
          'Cada categoria e a secção por trás dela',
          'Cada categoría y la sección que hay detrás',
        ),
        body: tx(
          'Press one to jump to its section, where the evidence and the regional ranking sit.',
          'Birine bastığınızda, kanıtların ve bölge sıralamasının bulunduğu bölüme gidersiniz.',
          'Naciśnij jedną, aby przejść do jej sekcji, gdzie są dowody i ranking regionalny.',
          'Carregue numa para ir à sua secção, onde estão as provas e o ranking regional.',
          'Pulsa una para ir a su sección, donde están las pruebas y el ranking regional.',
        ),
      },
      {
        anchor: 'actions',
        title: tx(
          'Take it with you, or compare',
          'Yanınızda götürün ya da karşılaştırın',
          'Zabierz ze sobą albo porównaj',
          'Leve consigo ou compare',
          'Llévatelo o compara',
        ),
        body: tx(
          'Add a few homes and read them side by side, or refresh this report when one of its sources publishes again.',
          'Birkaç konut ekleyip yan yana okuyun ya da kaynaklarından biri yeniden yayımladığında bu raporu yenileyin.',
          'Dodaj kilka mieszkań i czytaj je obok siebie albo odśwież ten raport, gdy jedno z jego źródeł opublikuje nowe dane.',
          'Adicione algumas casas e leia-as lado a lado, ou atualize este relatório quando uma das suas fontes publicar de novo.',
          'Añade varias viviendas y léelas una al lado de otra, o actualiza este informe cuando una de sus fuentes vuelva a publicar.',
        ),
      },
    ],
  });

  if (loading) {
    return (
      <div className="page page--narrow">
        <div className="card loading-panel">
          <Spinner
            label={tx(
              'Opening your report',
              'Raporunuz açılıyor',
              'Otwieranie raportu',
              'A abrir o seu relatório',
              'Abriendo tu informe',
            )}
          />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="page page--narrow stack">
        <ErrorPanel
          cause={error}
          title={tx(
            'We could not open this report',
            'Bu rapor açılamadı',
            'Nie udało się otworzyć tego raportu',
            'Não foi possível abrir este relatório',
            'No se ha podido abrir este informe',
          )}
          onRetry={retry}
        />
        <Link to="/" className="button button--secondary">
          {tx('Back to search', 'Aramaya dön', 'Wróć do wyszukiwania', 'Voltar à pesquisa', 'Volver a la búsqueda')}
        </Link>
      </div>
    );
  }

  const propertyAddressId = summary.propertyAddressId ?? detail?.propertyAddressId ?? null;
  const categories = detail ? orderedCategories(detail.categories) : [];
  const tasks = detail ? checklist(detail) : [];
  const checkedAt = detail?.generatedAt ?? summary.generatedAt;

  /*
   * What the EPC register actually said on this run, when the paid report has the source list.
   * Only used by the no-certificate branch below, where the difference between "it holds none"
   * and "it did not answer" changes what the reader should do. Null in the free tier, which has
   * no source list, so that branch keeps its general wording there.
   */
  const epcNote = detail?.sources.find((source) => source.sourceName === 'EPC')?.note ?? null;

  /*
   * The map is worth its space only when there is something to put on it - and "something" means
   * a thing with coordinates, not a thing in a list. Counting list lengths was wrong: a report
   * read from a server that does not yet send positions has full lists and nothing to draw, and
   * the section rendered as a heading over an empty box. The condition here is now the same one
   * the map itself applies.
   */
  const placed = (row: { latitude?: number | null; longitude?: number | null }) =>
    num(row.latitude) !== null && num(row.longitude) !== null;
  const hasSurroundings =
    num(summary.latitude) !== null
    && num(summary.longitude) !== null
    && [
      ...(summary.nearbySchools?.primary ?? []),
      ...(summary.nearbySchools?.secondary ?? []),
      ...(summary.nearbyTransport?.stations ?? []),
      ...(summary.nearbyTransport?.busStops ?? []),
    ].some(placed);

  const sections: Array<{ id: string; label: string }> = [
    ...categories.map((category) => ({
      id: `section-${category.category.toLowerCase()}`,
      label: categoryShort(category.category),
    })),
    ...(summary.homeInterior
      ? [{ id: 'section-interior', label: tx('The home itself', 'Konutun kendisi', 'Samo mieszkanie', 'A própria casa', 'La vivienda en sí') }]
      : []),
    // The energy section is always listed: when there is no certificate it says so, because
    // "no certificate" is itself something a tenant needs to know.
    { id: 'section-epc', label: tx('Energy certificate', 'Enerji sertifikası', 'Świadectwo energetyczne', 'Certificado energético', 'Certificado energético') },
    ...(summary.homeServices
      ? [{ id: 'section-utilities', label: tx('Utilities', 'Altyapı', 'Media', 'Serviços', 'Suministros') }]
      : []),
    ...(summary.councilTax
      ? [{ id: 'section-council-tax', label: tx('Council tax', 'Council tax', 'Podatek lokalny', 'Imposto municipal', 'Impuesto municipal') }]
      : []),
    ...(hasSurroundings
      ? [{ id: 'section-surroundings', label: tx('What is around it', 'Çevresinde ne var', 'Co jest w pobliżu', 'O que há à volta', 'Qué hay alrededor') }]
      : []),
    ...(summary.nearbyTransport
      ? [{ id: 'section-transport', label: tx('Getting around', 'Ulaşım', 'Komunikacja', 'Deslocações', 'Transporte') }]
      : []),
    ...(summary.nearbySchools
      ? [{ id: 'section-schools', label: tx('Schools', 'Okullar', 'Szkoły', 'Escolas', 'Colegios') }]
      : []),
    ...(tasks.length > 0
      ? [{ id: 'checklist', label: tx('Before you commit', 'İşlem öncesi', 'Przed decyzją',
          'Antes de avançar', 'Antes de decidir') }]
      : []),
    ...(detail
      ? [
          { id: 'section-method', label: tx('How the score works', 'Puan nasıl çalışır', 'Jak działa ocena', 'Como funciona a pontuação', 'Cómo funciona la puntuación') },
          { id: 'section-sources', label: tx('Sources', 'Kaynaklar', 'Źródła', 'Fontes', 'Fuentes') },
        ]
      : []),
  ];

  return (
    <div className="page page--report">
      <nav className="breadcrumb" aria-label={tx('Breadcrumb', 'Konum', 'Ścieżka', 'Navegação', 'Ruta')}>
        <Link to="/">
          <ChevronLeft size={15} aria-hidden="true" />
          {tx('Search', 'Arama', 'Szukaj', 'Pesquisar', 'Buscar')}
        </Link>
        <span aria-hidden="true">/</span>
        {/* The postcode goes to its area rather than being inert text: a reader deciding about
            one flat is usually also deciding about the streets around it. */}
        <Link to={`/areas?outcodes=${encodeURIComponent(summary.postcode.split(' ')[0] ?? summary.postcode)}`}>
          {summary.postcode}
        </Link>
      </nav>

      <ReportHead summary={summary} paid={detail !== null} />

      {/* Above the columns rather than inside one: on a phone the summary card comes first, and
          the score has to be read before it, not after a screenful of buttons. */}
      <ReportVerdict summary={summary} detail={detail} />

      <div className="report-layout">
        {/* One section open at a time: a dozen open sections is a page nobody reads to the end. */}
        <ReportSectionGroup>
        <div className="report-main">
          {summary.dataNotice ? (
            <Notice
              tone="warning"
              title={tx('About this data', 'Bu veriler hakkında', 'O tych danych', 'Sobre estes dados', 'Sobre estos datos')}
            >
              {summary.dataNotice}
            </Notice>
          ) : null}

          {detail ? <KeyFindings detail={detail} /> : null}

          <KeyFacts summary={summary} detail={detail} />

          {refreshError ? <ErrorPanel cause={refreshError} onRetry={refresh} /> : null}

          {awaitingPayment ? (
            <div className="card loading-panel">
              <Spinner
                label={tx(
                  'Confirming your payment with Stripe',
                  'Ödemeniz Stripe ile onaylanıyor',
                  'Potwierdzanie płatności w Stripe',
                  'A confirmar o pagamento com a Stripe',
                  'Confirmando el pago con Stripe',
                )}
              />
              <p className="small muted">
                {tx(
                  'This usually takes a few seconds. You can leave this page open; it will unlock itself.',
                  'Bu genellikle birkaç saniye sürer. Sayfayı açık bırakabilirsiniz; kendiliğinden açılacak.',
                  'Zwykle zajmuje to kilka sekund. Możesz zostawić tę stronę otwartą — odblokuje się sama.',
                  'Costuma demorar alguns segundos. Pode deixar esta página aberta; desbloqueia sozinha.',
                  'Suele tardar unos segundos. Puedes dejar esta página abierta; se desbloqueará sola.',
                )}
              </p>
            </div>
          ) : null}

          {detail ? (
            <>
              <ScoreTiles categories={categories} />
              {categories.map((category) => (
                <CategorySection
                  key={category.category}
                  category={category}
                  services={summary.homeServices}
                  checkedAt={checkedAt}
                />
              ))}
            </>
          ) : (
            <>
              <LockedTiles categories={summary.categorySummaries} />
              <UnlockPanel
                options={summary.unlockOptions}
                propertyAddressId={propertyAddressId}
                addressLine={summary.addressLine}
                bundleSlotsLeft={comparison?.bundleSlotsLeft ?? 0}
              />
            </>
          )}

          {summary.homeInterior ? (
            <HomeInteriorSection interior={summary.homeInterior} checkedAt={checkedAt} />
          ) : null}

          {summary.energyCertificate ? (
            <EnergyCertificateSection certificate={summary.energyCertificate} checkedAt={checkedAt} />
          ) : (
            /*
             * Said, not omitted. A section that simply disappears leaves the reader unable to tell
             * "this home has no certificate" from "we did not look" - and the first is a fact
             * worth knowing before renting, because an English home let without a valid EPC is
             * usually being let unlawfully.
             */
            <ReportSection
              id="section-epc"
              icon={Zap}
              title={tx('Energy certificate', 'Enerji sertifikası', 'Świadectwo energetyczne', 'Certificado energético', 'Certificado energético')}
              informational
              note={tx(
                'None found for this address',
                'Bu adres için bulunamadı',
                'Nie znaleziono dla tego adresu',
                'Nenhum encontrado para esta morada',
                'No se ha encontrado para esta dirección',
              )}
            >
              <p>
                {tx(
                  'The register holds no current energy certificate for this address, so nothing here describes its insulation, glazing, heating or running costs. That is a fact about the register, not an estimate: no figures have been guessed in their place.',
                  'Kayıt sisteminde bu adres için geçerli bir enerji sertifikası yok; bu yüzden yalıtımı, camları, ısıtması ve işletme maliyeti hakkında burada hiçbir şey yazmıyor. Bu, kayıt sistemine dair bir gerçektir, tahmin değil: yerine hiçbir rakam uydurulmadı.',
                  'Rejestr nie zawiera aktualnego świadectwa energetycznego dla tego adresu, więc nic tutaj nie opisuje izolacji, okien, ogrzewania ani kosztów. To fakt o rejestrze, nie oszacowanie: żadnych liczb nie zgadnięto w ich miejsce.',
                  'O registo não tem um certificado energético válido para esta morada, por isso nada aqui descreve o isolamento, as janelas, o aquecimento ou os custos. É um facto sobre o registo, não uma estimativa: não se inventaram números no lugar deles.',
                  'El registro no tiene un certificado energético vigente para esta dirección, así que aquí no se describe su aislamiento, sus ventanas, su calefacción ni sus costes. Es un hecho sobre el registro, no una estimación: no se ha inventado ninguna cifra en su lugar.',
                )}
              </p>
              {/*
                * The register's own words for this run, when the paid report has them.
                *
                * <p>"The register holds none" and "the register did not answer" are different
                * facts and lead a reader to do different things, but the prose above can only
                * state the first. The backend already distinguishes them in the source note - a
                * missing token, a register that stopped answering, and a register that answered
                * with nothing are three separate messages - so the honest move is to show that
                * note rather than parse it or guess which case this was.
                */}
              {epcNote ? <p className="small muted">{epcNote}</p> : null}

              <p className="small muted">
                {tx(
                  'In England a home may not normally be let without a valid certificate, so it is worth asking the landlord or agent for it.',
                  'İngiltere’de bir konut normalde geçerli bir sertifika olmadan kiraya verilemez; bu yüzden ev sahibinden ya da emlakçıdan istemekte fayda var.',
                  'W Anglii mieszkania zwykle nie wolno wynająć bez ważnego świadectwa, więc warto poprosić o nie właściciela lub agenta.',
                  'Em Inglaterra uma casa não pode normalmente ser arrendada sem certificado válido, por isso vale a pena pedi-lo ao senhorio ou à agência.',
                  'En Inglaterra una vivienda no suele poder alquilarse sin un certificado válido, así que conviene pedírselo al propietario o a la agencia.',
                )}
              </p>
            </ReportSection>
          )}

          {summary.homeServices ? (
            <HomeServicesSection services={summary.homeServices} checkedAt={checkedAt} />
          ) : null}

          {summary.councilTax ? (
            <CouncilTaxSection councilTax={summary.councilTax} checkedAt={checkedAt} />
          ) : null}

          {hasSurroundings ? (
            <ReportSection
              id="section-surroundings"
              icon={MapIcon}
              title={tx('What is around it', 'Çevresinde ne var', 'Co jest w pobliżu', 'O que há à volta', 'Qué hay alrededor')}
              note={tx(
                'The same schools and stops the lists below name, put where they actually are.',
                'Aşağıdaki listelerdeki okul ve durakların aynısı, gerçekte bulundukları yerde.',
                'Te same szkoły i przystanki co na listach poniżej, umieszczone tam, gdzie naprawdę są.',
                'As mesmas escolas e paragens das listas abaixo, no sítio onde estão.',
                'Los mismos colegios y paradas de las listas de abajo, situados donde están de verdad.',
              )}
              informational
              quality="verified"
              source={{
                name: tx(
                  'Get Information about Schools, and NaPTAN',
                  'Okul kayıtları (GIAS) ve NaPTAN',
                  'Rejestr szkół (GIAS) i NaPTAN',
                  'Registo de escolas (GIAS) e NaPTAN',
                  'Registro de colegios (GIAS) y NaPTAN',
                ),
                checked: checkedAt,
              }}
            >
              <SurroundingsMap
                latitude={summary.latitude}
                longitude={summary.longitude}
                addressLine={summary.addressLine}
                schools={summary.nearbySchools}
                transport={summary.nearbyTransport}
              />
            </ReportSection>
          ) : null}

          {summary.nearbyTransport ? (
            <NearbyTransportSection transport={summary.nearbyTransport} checkedAt={checkedAt} />
          ) : null}

          {summary.nearbySchools ? (
            <NearbySchoolsSection schools={summary.nearbySchools} checkedAt={checkedAt} />
          ) : null}

          {detail ? (
            <>
              <ChecklistCard items={tasks} />
              <ScoreMethod detail={detail} />
              <SourcesCard detail={detail} />
            </>
          ) : null}
        </div>
        </ReportSectionGroup>

        <ReportAside
          summary={summary}
          detail={detail}
          propertyAddressId={propertyAddressId}
          sections={sections}
          onRefresh={refresh}
          refreshing={refreshing}
        />
      </div>

      {/*
        * The actions again, at the foot.
        *
        * <p>On a phone the aside stacks after the sections, so the actions are already the last
        * thing on the page. From 900px up the aside becomes a side column and scrolls out of
        * sight, which leaves a reader who has just finished a long report with nothing to press.
        * This band only exists at that width - see .report-foot - so nothing is repeated twice on
        * a phone. It is not sticky on purpose: a bar pinned over a long report narrows the thing
        * being read on every scroll.
        */}
      <div className="report-foot">
        <ReportActions
          summary={summary}
          detail={detail}
          propertyAddressId={propertyAddressId}
          onRefresh={refresh}
          refreshing={refreshing}
        />
      </div>
    </div>
  );
}
